// MedsTimerChip.jsx — the roaming meds-countdown pill + its fire alert.
//
// Shown on every screen (rendered at the app shell) while Mackenzie's 1-hour
// timer is running. The countdown is derived from the STORED meds_given_at
// timestamp (see lib/morning.js), so it stays correct across tab nav, backgrounding,
// and force-close — the server-scheduled push is the source of truth for delivery;
// this chip is the in-app mirror. When the timer elapses while the app is open we
// also raise an iOS-timer-style modal (icon · "Mackenzie can eat 🐾" · Snooze/Stop)
// with a looping Web-Audio chime, per the prototype's fireMeds/stopMeds/snoozeMeds.

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { medsRemainingMs, medsIsRunning, formatCountdown, medsDurationMs } from "../lib/morning";
import { petClearLabel, medsPet } from "../lib/household.js";

const SNOOZE_MS = 5 * 60 * 1000;

// The timer's full length as "H:MM:SS" (e.g. 60min → "1:00:00") for the alert copy.
function formatDuration(ms) {
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// A short looping chime via Web Audio (no asset needed). Returns a stop fn.
function startChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return () => {};
    const ctx = new Ctx();
    let stopped = false;
    const beep = () => {
      if (stopped) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.42);
    };
    beep();
    const iv = setInterval(beep, 1400);
    return () => { stopped = true; clearInterval(iv); ctx.close().catch(() => {}); };
  } catch { return () => {}; }
}

export function MedsTimerChip({ store, onOpen }) {
  const session = store.morningSession;
  const prefs = store.preferences;
  const [now, setNow] = useState(() => new Date());
  const [firing, setFiring] = useState(false);
  const [snoozeUntil, setSnoozeUntil] = useState(0);
  const firedGuard = useRef(false);
  const stopChimeRef = useRef(null);

  // Whole feature is pet-scoped: with no pet configured there is no timer to
  // show and no alert to fire. Falls back to neutral wording rather than a name
  // if a pet exists but is only partly filled in.
  const pet = medsPet(prefs);
  const petName = pet?.name || "Pet";
  const petEmoji = pet?.emoji || "🐾";
  const clearLabel = petClearLabel(prefs) || "Medication timer done";

  const running = session?.meds_given_at ? medsIsRunning(session, prefs, now) : false;
  const remaining = session?.meds_given_at ? medsRemainingMs(session, prefs, now) : null;

  // Once-per-session guard so a reload doesn't re-fire an already-acknowledged alert.
  const guardKey = session ? `pronto:meds_fired:${session.id}` : null;

  // Tick every second while there's an active timer or a pending fire.
  useEffect(() => {
    if (!session?.meds_given_at) return;
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, [session?.meds_given_at]);

  // Detect the running → elapsed crossing while mounted, raise the alert once.
  useEffect(() => {
    if (!session?.meds_given_at || remaining == null) return;
    if (firedGuard.current) return;
    const already = guardKey && localStorage.getItem(guardKey);
    if (remaining <= 0 && !already) {
      if (snoozeUntil && now.getTime() < snoozeUntil) return;
      firedGuard.current = true;
      setFiring(true);
    }
  }, [remaining, session?.meds_given_at, guardKey, snoozeUntil, now]);

  // Chime lifecycle tied to the modal.
  useEffect(() => {
    if (firing) {
      stopChimeRef.current = startChime();
      return () => { stopChimeRef.current?.(); stopChimeRef.current = null; };
    }
  }, [firing]);

  const stop = () => {
    if (guardKey) { try { localStorage.setItem(guardKey, "1"); } catch {} }
    setFiring(false);
  };
  const snooze = () => {
    setSnoozeUntil(Date.now() + SNOOZE_MS);
    firedGuard.current = false;
    setFiring(false);
  };

  const showChip = running || (snoozeUntil && Date.now() < snoozeUntil && !firing);
  // No pet configured means this timer does not apply to this user at all.
  if (!pet) return null;
  if (!session?.meds_given_at) return null;

  return (
    <>
      <AnimatePresence>
        {showChip && !firing && (
          <motion.button
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            onClick={onOpen}
            style={{ boxShadow: "0 6px 16px -8px rgba(63,74,99,0.5)" }}
            className="fixed top-[calc(env(safe-area-inset-top,0px)+1.05rem)] right-[3.9rem] z-40 md:top-6 md:right-8
                       flex items-center gap-1.5 pl-2.5 pr-3 py-[5px] rounded-full
                       bg-[var(--pg-6)] backdrop-blur-[24px] border-[1.5px] border-pronto-navy
                       text-pronto-navy"
            aria-label={`${petName} meds timer`}
          >
            <span className="text-[14px] leading-none">{petEmoji}</span>
            <span className="font-round font-semibold text-[13px] tabular tracking-tight">
              {running ? formatCountdown(remaining) : "snoozed"}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {firing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center px-8 bg-black/30 backdrop-blur-sm"
          >
            <motion.div
              role="alertdialog"
              aria-label={clearLabel}
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              style={{ background: "rgba(250,250,252,0.95)", boxShadow: "0 24px 64px -20px rgba(0,0,0,0.55)" }}
              className="w-[262px] rounded-[20px] backdrop-blur-[24px] overflow-hidden text-center"
            >
              <div className="pt-5 pb-[15px] px-[18px]">
                {/* "p" monogram with the brand accent dot */}
                <div className="relative mx-auto mb-3 w-[46px] h-[46px] rounded-[12px] bg-pronto-logo grid place-items-center">
                  <span className="font-round font-bold text-white text-[26px] leading-none">p</span>
                  <span className="absolute bottom-[9px] right-[10px] w-1.5 h-1.5 rounded-full bg-pronto-navy-light" />
                </div>
                <div className="font-sf font-bold text-[17px] text-pronto-ink">{clearLabel}</div>
                <div className="text-[13px] text-pronto-text-2 mt-[3px]">
                  Medication timer · {formatDuration(medsDurationMs(prefs))}
                </div>
                <div className="inline-flex items-center gap-1.5 mt-[11px] px-2.5 py-1 rounded-full bg-[rgba(63,74,99,0.1)] text-pronto-navy font-sf text-[11px] font-semibold">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14" />
                  </svg>
                  Alert sound playing
                </div>
              </div>
              <div className="grid grid-cols-2 border-t border-[rgba(60,60,67,0.18)]">
                <button onClick={snooze}
                  className="py-[13px] text-[16px] text-pronto-navy border-r border-[rgba(60,60,67,0.18)] active:bg-black/5">
                  Snooze
                </button>
                <button onClick={stop}
                  className="py-[13px] text-[16px] text-pronto-navy font-bold active:bg-black/5">
                  Stop
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
